
import QueryString from 'qs';
import { ACCESS_KEY } from '../../../urls';
import { apiMode, urlLegacy, urlV2, endpoints  } from './apiConfig';

interface LegacyOk<T> { success: true; response: T }
interface LegacyErr { success: false; response?: any; error?: any }
type LegacyResp<T> = LegacyOk<T> | LegacyErr;

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string };

/** Keep server error payloads out of React children and callers' UI state. */
const errorText = (value: unknown, fallback = 'Request failed'): string => {
    if (typeof value === 'string' && value.trim()) return value;
    if (value != null && typeof value === 'object') {
        const error = value as { message?: unknown; name?: unknown };
        if (typeof error.message === 'string' && error.message.trim()) return error.message;
        if (typeof error.name === 'string' && error.name.trim()) return error.name;
        try { return JSON.stringify(value); } catch (_) { return fallback; }
    }
    return fallback;
};

const withTimeout = async <T>(p: Promise<T>, ms: number): Promise<T> =>
    await new Promise((resolve, reject) => {
        const id = setTimeout(() => { reject(new Error('REQUEST_TIMEOUT')); }, ms);
        p.then(v => { clearTimeout(id); resolve(v); })
            .catch(e => { clearTimeout(id); reject(e); });
    });


async function getV2<T>({
    path,
    query,
    signal,
}: {
    path: string;
    query?: string | Record<string, any>;
    signal?: AbortSignal;
}): Promise<ApiResult<T>> {
    try {
        const queryStr =
      query == null
          ? ''
          : typeof query === 'string'
              ? (query.startsWith('?') ? query : `?${query}`)
              : `?${QueryString.stringify(query, { encodeValuesOnly: true, arrayFormat: 'brackets' })}`;


    // const isAbsolute = /^https?:\/\//i.test(path);
    //     const finalUrl = `${isAbsolute ? '' : urlV2}${path}${queryStr}`;


    //     const res = await fetch(finalUrl, {
              const res = await fetch(`${urlV2}${path}${queryStr}`, {
            headers: getCommonHeaders(),
            signal,
        });
        const json = await res.json();
        if (!res.ok) return { ok: false as const, error: errorText(json?.error) };
        return { ok: true as const, data: json as T };
    } catch (e: any) {
        return { ok: false as const, error: e?.message ?? 'Network error' };
    }
}
async function legacyRequest<T>(opts: {
    collection: string;
    query?: string | Record<string, any>;
    id?: number;
    timeoutMs?: number;
    signal?: AbortSignal;
}): Promise<ApiResult<T>> {
    const { collection, query, id, signal } = opts;

    const body = {
        method: 'GET',
        collection,
        ...(id != null ? { id } : {}),
        ...(query != null
            ? { query: typeof query === 'string'
                ? query
                : QueryString.stringify(query, { encodeValuesOnly: true }) }
            : {}),
    };

    try {
        const res = await fetch(urlLegacy, {
            method: 'POST',
            headers: getCommonHeaders(),
            body: JSON.stringify(body),
            signal,
        });
        const json: LegacyResp<T> = await res.json();

        if ((json as any)?.success === true) {
            const payload = (json as any).response;
            if (payload?.error != null) {
                return { ok: false, error: JSON.stringify(payload.error) };
            }
            return { ok: true as const, data: payload as T };
        }
        return { ok: false as const, error: JSON.stringify(json) };
    } catch (e: any) {
        return { ok: false as const, error: e?.message ?? 'Network error' };
    }
}


/**
 * Единая точка входа: вызываем нужный транспорт в зависимости от режима.
 * adapter позволяет унифицировать форму данных (legacy -> data.data и т.п.)
 */
export async function apiFetch<T>({
    legacy,
    v2,
    adapter,
    timeoutMs = 25_000,
     noLimit,
}: {
    legacy?: { collection: string; query?: string | Record<string, any>; id?: number };
    v2?:     { path: string; params?: string | Record<string, any> };
    adapter?: (raw: any) => T;
    timeoutMs?: number;
    noLimit?: boolean; 
}): Promise<ApiResult<T>> {
    const run = async (): Promise<ApiResult<T>> => {
        console.log(apiMode,"apiMode")
        if (apiMode === 'agentLoader' && v2 != null) {
            const mergedParams =
        noLimit === true
          ? (typeof v2.params === 'string'
              ? `${v2.params}${v2.params.includes('?') ? '&' : ''}noLimit=true`
              : { ...(v2.params ?? {}), noLimit: true })
          : v2.params;

            const r = await getV2<any>({
                path: v2.path,
                query: mergedParams // v2.params,   
            });
            if (!r.ok) return r as ApiResult<T>;
            const data = (adapter != null) ? adapter(r.data) : (r.data as T);
            return { ok: true as const, data };
        } else if (legacy != null) {
            const r = await legacyRequest<any>({ ...legacy });
            if (!r.ok) return r as ApiResult<T>;
            const data = (adapter != null) ? adapter(r.data) : (r.data as T);
            return { ok: true as const, data };
        }
        return { ok: false as const, error: 'Misconfigured apiFetch' };
    };

    try {
        return await withTimeout<ApiResult<T>>(run(), timeoutMs);
    } catch (e: any) {
        return { ok: false as const, error: e?.message ?? 'Unknown error' };
    }
}






export const getCommonHeaders = (): HeadersInit => {
    const jwt = localStorage.getItem(ACCESS_KEY);
    return jwt
        ? {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
        }
        : {
            'Content-Type': 'application/json',
        };
};
